<template>
  <div class="shipment-rate-shop-tab" :class="tab.key">
    <RateShopBenchmarkRow
      v-model:freight-allowance="freightAllowanceInternal"
      :benchmarks="benchmarks"
      :rate-shop="rateShop"
      :show-freight-allowance="shouldShowFreightAllowance"
      :freight-allowance-editable="isFreightAllowanceEditable"
    />

    <RateShopServiceTypeFilters
      v-model="serviceTypeFilters"
      :rate-shop="rateShop"
    />

    <MessageContainer :messages="messages" message-class="rygen-message" />

    <RoutingGuide
      v-if="routingGuideRule"
      id="routing-guide"
      :rate-shop="rateShop"
      :service-type-filters="serviceTypeFilters"
      :rateable="isRateable"
      :service-type-filter-set="isAnyServiceTypeFilterSet"
      :disabled="disabled"
      editable
      @edit-quote="emit('edit-quote', $event)"
      @edit-spot-quote="emit('edit-spot-quote', $event)"
      @rate-selected="emit('rate-selected', $event)"
      @send-to-brokerage="emit('send-to-brokerage', $event)"
    />
    <RateShopTable
      v-else
      id="main-rates"
      :rate-quotes="filteredRateQuotes"
      :loading="rateShopLoading"
      :selectable="!disabled && isRateable"
      editable
      @edit-quote="emit('edit-quote', $event)"
      @edit-spot-quote="emit('edit-spot-quote', $event)"
      @rate-selected="emit('rate-selected', $event)"
      @send-to-brokerage="emit('send-to-brokerage', $event)"
    >
      <template #empty>
        {{ t('common.noParam', { param: t('rateShop.rates', 2) }) }}
      </template>
    </RateShopTable>

    <template v-if="showRateErrors">
      <h3>{{ t('rateShop.unrated') }}</h3>
      <RateShopTable
        id="rate-errors"
        :rate-quotes="rateErrors"
        :loading="rateShopLoading"
        price-class="text-danger font-medium"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Loadable } from '@rygen/rygen-ui/model';

import {
  useFreightAllowance,
  useRateShop,
  useRoutingGuide,
} from '@/composables';
import {
  FreightAllowance,
  type MessageSpecification,
  RateQuote,
  RateShop,
  RateShopStatus,
  type ServiceTypeFilters,
  Shipment,
  ShipmentStatus,
} from '@/model';
import { useCreditUsageStore } from '@/stores/CreditUsage';

import RateShopBenchmarkRow from '@/components/rateShop/RateShopBenchmarkRow.vue';
import RateShopServiceTypeFilters from '@/components/rateShop/RateShopServiceTypeFilters.vue';
import RateShopTable from '@/components/rateShop/RateShopTable.vue';
import RoutingGuide from '@/components/rateShop/RoutingGuide.vue';
import { ShipmentRateShopTab } from '@/components/shipment/rateShop/ShipmentRateShopHeader.vue';
import MessageContainer from '@/components/ui/MessageContainer.vue';

const serviceTypeFilters = defineModel('serviceTypeFilters', {
  type: Object as PropType<ServiceTypeFilters>,
  default: () => ({}),
});

const props = defineProps({
  tab: {
    type: Object as PropType<ShipmentRateShopTab>,
    required: true,
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    default: () => ({ loading: false }),
  },
  freightAllowance: {
    type: Object as PropType<FreightAllowance | undefined>,
    default: () => undefined,
  },
  rateShopLoading: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { tab, shipment, rateShop, rateShopLoading, freightAllowance, disabled } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'edit-quote', rateQuote: RateQuote): void;
  (event: 'edit-spot-quote', rateQuote: RateQuote): void;
  (event: 'rate-selected', rateQuote: RateQuote): void;
  (event: 'rate-shop-completed'): void;
  (event: 'send-to-brokerage', rateQuote: RateQuote): void;
  (
    event: 'update:freight-allowance',
    freightAllowance?: FreightAllowance,
  ): void;
}>();

const { creditUsage } = useCreditUsageStore();
const {
  freightAllowanceInternal,
  isFreightAllowanceEditable,
  shouldShowFreightAllowance,
} = useFreightAllowance(freightAllowance, emit);
const { t } = useI18n();
const {
  benchmarks,
  filteredRateQuotes,
  isAnyServiceTypeFilterSet,
  rateErrors,
  showRateErrors,
  requiresReRate,
} = useRateShop(rateShop, {
  serviceTypeFilters,
  acceptedRateQuoteId: toRef(() => shipment.value.acceptedRateQuoteId),
});
const { routingGuideRule } = useRoutingGuide(rateShop, serviceTypeFilters);

const isRateable = computed(() => {
  return shipment.value.status === ShipmentStatus.PLANNING;
});

const lineItemsMissingLtlInfo = computed(() => {
  const lineItemsMissingFreightClass = shipment.value.lineItems.some(
    (li) => !li.freightClass,
  );
  const lineItemsMissingDimensions = shipment.value.lineItems.some(
    (li) => !li.heightDim || !li.widthDim || !li.lengthDim,
  );
  return lineItemsMissingDimensions || lineItemsMissingFreightClass;
});

const shouldShowMissingLtlInfoBanner = computed(() => {
  return lineItemsMissingLtlInfo.value && !rateShopLoading.value;
});

const messages = computed((): MessageSpecification[] => [
  {
    id: 'approaching-credit-limit-warning',
    text: t('creditLimits.approachingLimitMessage'),
    severity: 'warn',
    icon: 'pi pi-exclamation-triangle',
    visible:
      !creditUsage.value.loading &&
      creditUsage.value.data?.standing === 'APPROACHING_LIMIT',
  },
  {
    id: 'exceeded-credit-limit-warning',
    text: t('creditLimits.exceededLimitMessage'),
    severity: 'error',
    icon: 'pi pi-exclamation-triangle',
    visible:
      !creditUsage.value.loading &&
      creditUsage.value.data?.limitExceeded === true,
  },
  {
    id: 'invalid-rates-warning',
    text: t('common.concatParams', {
      param1: t('rateShop.invalidRates'),
      param2: isRateable ? t('rateShop.requiresReRate') : '',
    }),
    severity: 'warn',
    visible: requiresReRate.value && !rateShopLoading.value,
  },
  {
    id: 'missing-ltl-info-warning',
    text: t('lineItems.missingLtlInfoMessage'),
    severity: 'info',
    visible: shouldShowMissingLtlInfoBanner.value,
  },
]);

watch(
  () => rateShop.value.data?.status,
  (newVal?: RateShopStatus) => {
    if (newVal === RateShopStatus.COMPLETED) {
      emit('rate-shop-completed');
    }
  },
);
</script>
